// Bau und Wiederaufbau des IndexedDB-Session-Records — rein, ohne Store-
// oder IDB-Zugriff, damit er ohne DOM testbar ist. useSessionPersistence
// liefert den Live-Stand (getState()) und schreibt das Ergebnis.

import type { Asset, Block, SessionState } from '@shared/types';
import { persistableVideoDurations } from '@/lib/probeVideoDuration';
import { SESSION_RECORD_FORMAT, type StoredSession } from '@/lib/indexeddb';
import { splitSessionAudio } from '@/lib/sessionAudio';
import { combinedAssetIdOrder } from '@/lib/sessionRemap';
import { applyVoiceIdsMigration } from '@/lib/voiceMigration';
import { ensureSessionBlockIds } from '@/lib/blockId';
import { migrateSessionModels } from '@/lib/modelMigration';

/** Was buildSessionRecord vom Session-Store braucht (Daten, keine Setter). */
export type SessionStoreSnapshot = SessionState & { config: { assets: Asset[] } | null };

/** Extract just the `SessionState` slice of the store. Zustand's store has
 *  extra fields (toolId/config/loaded/setters) that aren't part of the
 *  SessionState contract; we need only the data. */
export function sliceSession(s: SessionState): SessionState {
  return {
    name: s.name,
    sessionFolder: s.sessionFolder,
    shortId: s.shortId,
    currentAssetIdx: s.currentAssetIdx,
    blocks: s.blocks,
    assetBlocks: s.assetBlocks,
    stsAssets: s.stsAssets,
    customAssets: s.customAssets,
    customDurations: s.customDurations,
    customVideoEnabled: s.customVideoEnabled,
    freeMode: s.freeMode,
    freeBlocks: s.freeBlocks,
    freeActiveIdx: s.freeActiveIdx,
    versions: s.versions,
  };
}

/** Record + Audio-Map aus einem Store-Stand. `persistKey` ist der keyPath
 *  (toolId bzw. toolId:LANG bei Multi-Lingual), NICHT zwingend das `toolId`
 *  des Stores. */
export function buildSessionRecord(
  state: SessionStoreSnapshot,
  videoDurations: Record<number, number>,
  persistKey: string,
  now: Date,
): { record: StoredSession; audio: ReturnType<typeof splitSessionAudio>['audio'] } {
  const assets = [...(state.config?.assets ?? []), ...state.customAssets];
  const { session, audio } = splitSessionAudio(sliceSession(state));
  const record: StoredSession = {
    // `toolId` is the StoredSession keyPath (see indexeddb.ts) — it holds
    // persistKey (toolId, or toolId:LANG for multilingual projects), NOT
    // necessarily the session store's own `toolId` field.
    toolId: persistKey,
    savedAt: now.toISOString(),
    format: SESSION_RECORD_FORMAT,
    session,
    // Bekannte Dauern der Cloud-Videos (Audit H6) — damit Export/Upload
    // nach einem Reload nicht auf targetDuration/30 s zurückfallen.
    video: { durations: persistableVideoDurations(videoDurations, assets) },
    assetIdOrder: combinedAssetIdOrder(state.config?.assets, state.customAssets),
  };
  return { record, audio };
}

export function rehydrateSession(stored: SessionState): SessionState {
  // Alt-Bestand ohne Block-IDs bekommt sie hier nach (Audit M7); Blöcke
  // aus Eleven-v3-Zeiten laufen auf v4 (2026-09-29, lib/modelMigration.ts).
  const s = migrateSessionModels(ensureSessionBlockIds(stored));
  const assetBlocks: Record<number, Block[]> = {};
  for (const [k, v] of Object.entries(s.assetBlocks)) assetBlocks[Number(k)] = v;
  return {
    ...s,
    assetBlocks,
    customAssets: applyVoiceIdsMigration(s.customAssets ?? []),
  };
}

/** A stored session is "meaningful" if the user progressed past Welcome
 *  (has a name) AND actually did something (any block with text/takes,
 *  any STS take, any recorded version). A bare name alone still counts
 *  because the user opened the project and we should offer to resume. */
export function hasMeaningfulState(s: SessionState): boolean {
  if (s.name && s.name.trim().length > 0) return true;
  const blockHasWork = (b: Block) =>
    b.text.trim().length > 0 || b.takes.length > 0;
  const anyAssetBlocks = Object.values(s.assetBlocks).some((arr) =>
    arr.some(blockHasWork),
  );
  if (anyAssetBlocks) return true;
  if (s.blocks.some(blockHasWork)) return true;
  if (Object.values(s.stsAssets).some((a) => a.takes.length > 0)) return true;
  if (Object.values(s.versions).some((v) => v.length > 0)) return true;
  return false;
}

